// ============================================================
// Shop.js
// The workshop screen. The ONLY screen a 'shop' user can reach.
//
// BUILT FOR A TABLET ON A BENCH: someone standing up, at arm's length, in
// gloves, with dirty hands. That drives every decision here — large type, large
// targets, no sidebar, no dense tables, and status you can read across a room.
// Nothing on this screen assumes a mouse or good light.
//
// It is NOT a stripped-down version of the office UI. A welder gets a screen
// built for the job, not the salesperson's screen with things missing.
//
// TWO JOBS, TWO SECTIONS. The shop builds gates and pulls material, and those
// are different tasks with different rhythms. Tabs rather than one merged list:
// a welder at the bench and a man filling an order are not looking for the same
// thing, and interleaving them would make both harder to scan.
//
// LIVE: both lists are onSnapshot subscriptions, the same pattern
// NotificationBell.js uses, so two welders on two tablets see each other's
// changes without either of them reloading.
//
// STRICTLY READ-ONLY, except for progress and the pickup signature.
// This screen renders drawings and invoices from stored data and cannot alter
// either. There is no save, edit, delete or duplicate control anywhere in it.
//
// ONE INPUT, AND ONLY ONE. The screen carried no input, select, textarea or
// contenteditable at all until signature capture arrived; it now has exactly
// one, the name field inside the signature pad, and it appears only while the
// pad is open. A signature nobody can read is worth little without a name typed
// beside it, so that field is the price of the feature. Everywhere else the
// no-input rule still holds — the collected ticks are buttons drawn as
// checkboxes for exactly this reason.
//
// There are FOUR writes, and every one of them records what happened rather
// than changing the work:
//   setStatus()     — shopStatus, shopStatusBy, shopStatusAt on a drawing
//   setPickStatus() — pickStatus, pickStatusBy, pickStatusAt on an invoice
// Nothing here touches inventory. Picking is "the material is gathered";
// deducting is "the stock figures reflect it", the office does the second, and
// making one imply the other would put the count wrong.
//
// NO PRICES. The picking view shows quantity, unit, description and SKU and
// nothing financial. A picking list is not an invoice, and a welder carrying a
// printed sheet around a yard should not be carrying the customer's pricing.
//
// COLLECTED TICKS are the third write: pickedItemIds, and nothing else. A tick
// records that the customer took that line. It does NOT touch inventory — the
// stock went out of the count when the office deducted the invoice, and ticking
// here would deduct it a second time.
//
// THE PICKUP SIGNATURE is the fourth: pickupSignature, pickupSignedName,
// pickupSignedAt and pickupWitnessedBy, built by SignaturePad.js so the office
// and the shop cannot write different shapes. It is stroke data as an SVG path
// string — a few kilobytes of text inside the invoice document, no Firebase
// Storage involved. It records that a person received goods; it does not decide
// whether the order is Ready and it does not move stock.
// ============================================================

import React, { useState, useEffect, useMemo } from 'react';
import { signOut } from 'firebase/auth';
import { collection, query, where, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useRole } from '../hooks/useRole';
import { shopStatusFields } from '../utils/shopStatus';
import GateSheet from '../components/GateSheet';
import SignaturePad, { buildSignatureFields, hasSignature, PrintSignatureBlock } from '../components/SignaturePad';
import {
  PICK_STATUS, resolvePickStatus, pickStatusFields,
  isItemPicked, toggleItemFields, backOrderCount, collectableCount,
} from '../utils/pickStatus';

// Deliberately higher-contrast than the office palette: this is read in a
// workshop, not at a desk.
const C_BG = '#EEF4FA';
const C_CARD = '#FFFFFF';
const C_BORDER = '#D3E0EC';
const C_TEXT = '#03045E';
const C_TEXT_2 = '#54657A';
const C_ACCENT = '#0077B6';

// Status is carried by COLOUR AND TEXT AND SHAPE — never colour alone. A
// workshop has bad light, and colour blindness is common.
//
// KEY vs LABEL — they deliberately differ.
//
// The KEY is the value STORED in Firestore. The LABEL is only what a screen
// says. Renaming a key would mean migrating every record already written, and
// until that migration finished every older document would resolve to nothing
// and silently show the fallback — so the stored vocabulary is frozen and the
// wording is changed here, where it costs nothing.
//
// So 'Pending' is the stored value and "Submitted" is what everyone reads.
const STATUSES = [
  { key: 'Pending', label: 'Submitted', icon: '○', bg: '#FDEAEA', fg: '#8C2020', solid: '#A32D2D' },
  { key: 'In Progress', label: 'In Progress', icon: '◐', bg: '#FFF3DC', fg: '#7A4E00', solid: '#B26A00' },
  { key: 'Complete', label: 'Complete', icon: '●', bg: '#E4F5EC', fg: '#0B5B44', solid: '#0F6E56' },
];
const statusOf = (d) => STATUSES.find(s => s.key === d?.shopStatus) || STATUSES[0];

// The two states an order can be in, taken from the shared module so the shop
// and the office cannot disagree about what "Ready" means. Only the solid
// colour — for the big buttons — is added here; the label, glyph and tints all
// come from PICK_STATUS.
const PICK_KEYS = ['Pending', 'Ready'];
const PICK_SOLID = { 'Pending': '#B26A00', 'Ready': '#0F6E56' };
const pickOf = (inv) => {
  const key = resolvePickStatus(inv);
  return { key, ...PICK_STATUS[key], solid: PICK_SOLID[key] };
};

// ── THREE COLUMNS OUT OF TWO STORED STATES ───────────────────────────
//
// This is the one place in the app where the board's three columns and the
// invoice's two stored words have to be reconciled, so the rule is written out
// once, here, and nothing else decides it.
//
// pickStatus is 'Pending' or 'Ready' and NOTHING ELSE — see pickStatus.js. There
// is no stored 'Back order'. That middle column is DERIVED, every render, from
// which line items are ticked. Storing it would mean a third word to migrate
// and two facts that can disagree; deriving it means it is always right.
//
// THE RULE, in order:
//
//   1. PARTLY COLLECTED WINS. At least one line ticked and at least one not
//      ticked puts the order in Back order, whatever its pickStatus says.
//      "The customer took some of this and is coming back" is the single most
//      useful thing to see about an order, and it is true whether or not the
//      shop has finished gathering it.
//
//   2. OTHERWISE THE STORED STATUS DECIDES. 'Ready' goes right, 'Pending' goes
//      left. So a fully ticked order sits in Ready only if a person marked it
//      Ready; ticking the last line does NOT move it there by itself. Ticks
//      record what the customer carried out — a person decides when the shop's
//      work is done, and arithmetic must not make that call for them.
//
// ONE DELIBERATE DIVERGENCE from a literal reading of "fully unchecked goes in
// Not pulled": an order marked Ready with nothing ticked stays in READY, not
// Not pulled. Those are different facts — Ready means the shop gathered it,
// ticks mean the customer took it — and an order gathered this morning that
// nobody has collected yet is the normal case, not an unpulled one. Sending it
// back to Not pulled would have the shop pick the same order twice.
//
// An order with no collectable lines at all has nothing to be part-collected,
// so it falls to rule 2.
const PICK_COLUMNS = [
  { key: 'pending', label: 'Not pulled', icon: '🟠', bg: '#FFF3DC', fg: '#7A4E00', solid: '#B26A00' },
  { key: 'backorder', label: 'Back order', icon: '⬚', bg: '#FDEAEA', fg: '#8C2020', solid: '#A32D2D' },
  { key: 'ready', label: 'Ready', icon: '🟢', bg: '#E4F5EC', fg: '#0B5B44', solid: '#0F6E56' },
];

const pickColumnOf = (order) => {
  const outstanding = backOrderCount(order);
  const collected = collectableCount(order) - outstanding;
  if (collected > 0 && outstanding > 0) return 'backorder';          // rule 1
  return resolvePickStatus(order) === 'Ready' ? 'ready' : 'pending';  // rule 2
};

const fmtDateTime = (v) => {
  if (!v) return '';
  const d = v?.toDate ? v.toDate() : new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const fmtDate = (v) => {
  if (!v) return '—';
  const d = v?.toDate ? v.toDate() : new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
// Trailing zeros dropped, two decimals kept — quarter inches must survive.
const inches = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? `${Math.round(n * 100) / 100}"` : '—';
};

export default function Shop() {
  const { userName } = useRole();
  const [drawings, setDrawings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [openDrawing, setOpenDrawing] = useState(null);
  const [savingId, setSavingId] = useState(null);

  // ── Sections ──
  // Gates opens by default: fabrication is the shop's standing work, and an
  // order arrives as an interruption to it. The Orders tab carries a count, so
  // waiting work announces itself without the screen having to change shape.
  // Deliberately NOT "open whichever has work" — a shared tablet that greets
  // two different people with two different screens is a tablet nobody trusts.
  const [tab, setTab] = useState('gates');

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState(false);
  const [openOrder, setOpenOrder] = useState(null);
  const [pickBusyId, setPickBusyId] = useState(null);

  // Whether the signature pad is open on the order currently being viewed, and
  // whether a save is in flight. Not keyed by order id: the pad only ever
  // appears inside the one order's item view.
  const [signing, setSigning] = useState(false);
  const [sigBusy, setSigBusy] = useState(false);

  // Sheet zoom, as a width multiplier.
  //
  // GateSheet is an 850x620 viewBox drawn for landscape paper. At 100% it fills
  // the screen width, which on a portrait tablet leaves the 9pt data-column text
  // around 6px tall — present, but not readable at arm's length in a workshop.
  // Explicit +/- controls rather than the browser's own zoom: pinching zooms the
  // whole page, moves the close button off-screen and is easy to get stuck in.
  // These scale only the sheet, inside its own scroller, and Fit puts it back.
  const [zoom, setZoom] = useState(1);
  const ZOOM_STEPS = [1, 1.5, 2, 3, 4];
  const zoomIn = () => setZoom(z => ZOOM_STEPS.find(v => v > z) ?? z);
  const zoomOut = () => setZoom(z => [...ZOOM_STEPS].reverse().find(v => v < z) ?? z);

  // ── Live list ──
  // One collection, no filter, no orderBy — so no composite index, and sorting
  // happens below. onSnapshot returns its own unsubscribe; returning it
  // straight out of the effect makes React tear the listener down on unmount.
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'shopDrawings'),
      (snap) => {
        setDrawings(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
        setError(false);
      },
      (err) => {
        console.error('Shop drawing listener failed:', err);
        setError(true);
        setLoading(false);
      },
    );
    return unsubscribe;
  }, []);

  // ── Live orders ──
  // ONE equality filter and no orderBy, so Firestore's automatic single-field
  // index covers it — nothing to deploy — and ordering happens below.
  //
  // The where() is what keeps the shop's list honest: an invoice reaches this
  // screen only because the office deliberately sent it. Everything else in the
  // collection is invisible here.
  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, 'invoices'), where('pickRequested', '==', true)),
      (snap) => {
        setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setOrdersLoading(false);
        setOrdersError(false);
      },
      (err) => {
        console.error('Order listener failed:', err);
        setOrdersError(true);
        setOrdersLoading(false);
      },
    );
    return unsubscribe;
  }, []);

  // Newest first. createdAt may be a Firestore Timestamp on newer records and
  // an ISO string on older ones, so both are reduced to millis before sorting.
  const sorted = useMemo(() => {
    const millis = (v) => {
      if (!v) return 0;
      if (v.toDate) return v.toDate().getTime();
      const t = new Date(v).getTime();
      return Number.isFinite(t) ? t : 0;
    };
    return [...drawings].sort((a, b) => millis(b.createdAt) - millis(a.createdAt));
  }, [drawings]);

  // Pending before Ready, newest first inside each. Work still to do sits at the
  // top; what is already picked stays visible below rather than disappearing,
  // because "did I already do that one" is a question worth being able to answer.
  const sortedOrders = useMemo(() => {
    const rank = (o) => (resolvePickStatus(o) === 'Ready' ? 1 : 0);
    const millis = (v) => {
      if (!v) return 0;
      if (v.toDate) return v.toDate().getTime();
      const t = new Date(v).getTime();
      return Number.isFinite(t) ? t : 0;
    };
    return [...orders].sort((a, b) =>
      rank(a) - rank(b) ||
      millis(b.pickRequestedAt || b.createdAt) - millis(a.pickRequestedAt || a.createdAt));
  }, [orders]);

  // Outstanding only — a count of finished work is not news.
  const gatesOutstanding = useMemo(
    () => sorted.filter(d => (d.shopStatus || 'Pending') !== 'Complete').length, [sorted]);

  // ── The board ──
  // One pass over the already-sorted list rather than three filters, so the
  // newest-first order inside each column is the same order `sorted` produced
  // and nothing has to be re-sorted per column.
  //
  // An unrecognised shopStatus falls into Submitted, matching statusOf(). A
  // drawing must never vanish because its status word is one nobody expects —
  // an invisible gate is a gate that does not get built.
  const columns = useMemo(() => {
    const by = { 'Pending': [], 'In Progress': [], 'Complete': [] };
    sorted.forEach(d => (by[d.shopStatus] || by['Pending']).push(d));
    return by;
  }, [sorted]);

  // The same one-pass partition for orders. sortedOrders is already
  // Pending-before-Ready and newest-first, and filtering preserves that, so
  // every column comes out newest-first without re-sorting.
  const orderColumns = useMemo(() => {
    const by = { pending: [], backorder: [], ready: [] };
    sortedOrders.forEach(o => by[pickColumnOf(o)].push(o));
    return by;
  }, [sortedOrders]);
  const ordersOutstanding = useMemo(
    () => orders.filter(o => resolvePickStatus(o) !== 'Ready').length, [orders]);

  const setStatus = async (drawing, status) => {
    if (drawing.shopStatus === status) return;
    setSavingId(drawing.id);
    try {
      // The SAME writer the office uses (utils/shopStatus.js). The office can
      // change this status too now, and two inline copies of the same three
      // fields is exactly how the two would drift apart.
      await updateDoc(doc(db, 'shopDrawings', drawing.id), shopStatusFields(userName, status));
      // No local state update: the listener above is already watching this
      // document and pushes the change back, on this tablet and every other.
    } catch (err) {
      console.error('Could not update shop status:', err);
      alert('Could not save. Check the connection and try again.');
    }
    setSavingId(null);
  };

  // The shop's half of the pick handshake.
  //
  // Both the fields and the vocabulary come from pickStatus.js — this screen
  // neither decides which three fields to write nor invents a state the office
  // would not recognise. It supplies who and what; the module supplies the
  // shape and the timestamp.
  const setPickStatus = async (invoice, status) => {
    if (resolvePickStatus(invoice) === status) return;
    setPickBusyId(invoice.id);
    try {
      await updateDoc(doc(db, 'invoices', invoice.id), pickStatusFields(userName, status));
      // No local update: the listener is already watching this document.
    } catch (err) {
      console.error('Could not update pick status:', err);
      alert('Could not save. Check the connection and try again.');
    }
    setPickBusyId(null);
  };

  // Tick or untick a collected line.
  //
  // Writes pickedItemIds and NOTHING else — no quantity, no status, no
  // inventory. Deliberately does not touch pickStatus either: whether an order
  // is Ready is a person's decision, not an arithmetic one. A customer can
  // leave with most of an order and the shop can still call it done, or take
  // everything and the shop can still be checking.
  const toggleCollected = async (invoice, item) => {
    const fields = toggleItemFields(invoice, item?.id);
    if (!fields) return;   // no id — cannot be ticked; see the note in pickStatus.js
    try {
      await updateDoc(doc(db, 'invoices', invoice.id), fields);
      // No local update: the listener is already watching this document, and it
      // is what keeps a second tablet in step.
    } catch (err) {
      console.error('Could not update collected items:', err);
      alert('Could not save. Check the connection and try again.');
    }
  };

  // Save a pickup signature.
  //
  // Writes the four signature fields and NOTHING else. Deliberately does not
  // touch pickStatus: a signature is the customer's receipt, not the shop's
  // declaration that it has finished, and a partial pickup gets signed for too.
  // Deliberately does not touch pickedItemIds either — what the customer took
  // is recorded by the ticks, one line at a time, and re-deriving it from a
  // signature would guess.
  //
  // RE-SIGNING OVERWRITES. buildSignatureFields stamps a fresh pickupSignedAt
  // every time, so signing again replaces the strokes, the name and the time
  // together and there is never a signature sitting under an old timestamp.
  const saveSignature = async (order, { path, name }) => {
    const fields = buildSignatureFields({ path, name, witnessedBy: userName });
    if (!fields) return;   // no stroke or no name — the pad already blocks this
    setSigBusy(true);
    try {
      await updateDoc(doc(db, 'invoices', order.id), fields);
      setSigning(false);
      // No local update: the listener is already watching this document.
    } catch (err) {
      console.error('Could not save the signature:', err);
      alert('Could not save. Check the connection and try again.');
    }
    setSigBusy(false);
  };

  // Every drawing opens at Fit. Inheriting the previous sheet's zoom would
  // leave a welder looking at the middle of a drawing he has not seen whole.
  const openSheet = (d) => { setZoom(1); setOpenDrawing(d); };

  const handleSignOut = async () => {
    if (window.confirm('Sign out of this tablet?')) await signOut(auth);
  };

  // ── Full-screen sheet ──
  if (openDrawing) {
    const d = sorted.find(x => x.id === openDrawing.id) || openDrawing;
    const s = statusOf(d);
    return (
      <div style={{ minHeight: '100vh', background: C_BG, fontFamily: 'Manrope, sans-serif' }}>
        <style>{`
          @media print {
            /* Everything but the sheet goes. Same result as the office print
               path: paper carries the drawing and nothing else. */
            .shop-chrome { display: none !important; }
            body { background: #FFFFFF !important; }
            /* Zoom is a screen affordance. Paper gets the sheet at its own
               size, un-scrolled, whatever the welder was zoomed to. */
            .shop-sheet-scroll { overflow: visible !important; }
            .shop-sheet-inner { width: 100% !important; }
            .shop-sheet-card { border: none !important; padding: 0 !important; }
          }
          @page { size: landscape; margin: 0.4in; }
        `}</style>

        <div className="shop-chrome" style={{
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          padding: 16, background: C_CARD, borderBottom: `2px solid ${C_BORDER}`,
        }}>
          {/* Close is first, largest and red-bordered: a welder must never feel
              trapped in this view. */}
          <button onClick={() => setOpenDrawing(null)} style={btnClose}>✕ Close</button>
          <button onClick={() => window.print()} style={btnBig}>🖨 Print</button>
          <span style={{ color: C_TEXT, fontSize: 22, fontWeight: 800, marginLeft: 4 }}>
            {d.drawingNumber}
          </span>
          <span style={{ ...pill(s), marginLeft: 'auto' }}>{s.icon} {s.label.toUpperCase()}</span>
        </div>

        {/* Zoom. Buttons rather than a pinch gesture, because a pinch zooms the
            page and takes the Close button with it. */}
        <div className="shop-chrome" style={{
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          padding: '12px 16px', background: C_CARD, borderBottom: `2px solid ${C_BORDER}`,
        }}>
          <button onClick={zoomOut} disabled={zoom <= ZOOM_STEPS[0]} style={btnZoom(zoom <= ZOOM_STEPS[0])}>−</button>
          <span style={{
            color: C_TEXT, fontSize: 20, fontWeight: 800, minWidth: 76, textAlign: 'center',
          }}>
            {Math.round(zoom * 100)}%
          </span>
          <button onClick={zoomIn} disabled={zoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1]} style={btnZoom(zoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1])}>+</button>
          <button onClick={() => setZoom(1)} style={{ ...btnBig, minWidth: 110 }}>Fit</button>
        </div>

        <div className="shop-chrome" style={{ display: 'flex', gap: 12, padding: 16, flexWrap: 'wrap' }}>
          {/* Status is reachable from here too, so the sheet can be marked
              complete the moment it has been read — no trip back to the list. */}
          {STATUSES.map(st => (
            <button
              key={st.key}
              onClick={() => setStatus(d, st.key)}
              disabled={savingId === d.id}
              style={statusButton(st, d.shopStatus === st.key, savingId === d.id)}
            >
              {st.icon} {st.label}
            </button>
          ))}
        </div>

        <div style={{ padding: 16 }}>
          <div className="shop-sheet-card" style={{ background: '#FFFFFF', border: `1px solid ${C_BORDER}`, borderRadius: 12, padding: 12 }}>
            {/* The sheet scales itself to whatever width the inner div is —
                GateSheet renders width="100%" over an 850x620 viewBox — so
                zooming is a width multiplier and the scroller carries the
                overflow. touchAction leaves native panning and pinching alone
                INSIDE the scroller for anyone who prefers it. */}
            <div
              className="shop-sheet-scroll"
              style={{ overflow: 'auto', WebkitOverflowScrolling: 'touch', touchAction: 'pan-x pan-y pinch-zoom' }}
            >
              <div className="shop-sheet-inner" style={{ width: `${zoom * 100}%` }}>
            {/* The same sheet the office prints — not a shop-only rebuild.
                Rendered from the STORED record: this is a viewer, and there is
                no path from here back into the drawing's data. */}
            <GateSheet
              gate={d}
              meta={{
                drawingNumber: d.drawingNumber,
                customerName: d.customerName,
                frameFinish: d.frameFinish,
                meshFinish: d.meshFinish,
                meshGauge: d.meshGauge,
                dateText: fmtDate(d.createdAt),
                timeText: '',
                drawnBy: d.drawnBy,
                documentRef: d.documentRef,
              }}
              blankSheet={!!d.blankSheet}
            />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Full-screen picking list ──
  //
  // NOT a reuse of ViewDocument's renderPackingSlipBody, and it could not be:
  // that function is a closure inside ViewDocument, reading its `document`,
  // `customerPhone`, `formatMoney4` and `lineTotal` from scope, and it is not
  // exported. Extracting it would mean editing ViewDocument, which this change
  // does not touch. And even lifted, it carries Unit Price, Total and a totals
  // block — everything this view must not show — so it would have had to be
  // forked and stripped rather than reused.
  //
  // What is reused is its SHAPE: the same Qty / UM / Description columns, the
  // same text-note and NON-STOCK treatments, so a welder reading this screen
  // and a welder reading the printed slip are reading the same document. If the
  // two ever need to stay in step properly, the packing-slip body should be
  // extracted into a component both can render — worth doing, out of scope here.
  if (openOrder) {
    const o = sortedOrders.find(x => x.id === openOrder.id) || openOrder;
    const ps = pickOf(o);
    const items = Array.isArray(o.lineItems) ? o.lineItems : [];
    return (
      <div style={{ minHeight: '100vh', background: C_BG, fontFamily: 'Manrope, sans-serif' }}>
        <style>{`
          @media print {
            .shop-chrome { display: none !important; }
            body { background: #FFFFFF !important; }
            .shop-pick-card { border: none !important; padding: 0 !important; }
          }
          @page { size: portrait; margin: 0.5in; }
        `}</style>

        <div className="shop-chrome" style={{
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          padding: 16, background: C_CARD, borderBottom: `2px solid ${C_BORDER}`,
        }}>
          <button onClick={() => setOpenOrder(null)} style={btnClose}>✕ Close</button>
          <button onClick={() => window.print()} style={btnBig}>🖨 Print</button>
          <span style={{ color: C_TEXT, fontSize: 22, fontWeight: 800, marginLeft: 4 }}>
            #{o.docNumber}
          </span>
          <span style={{ ...pill(ps), marginLeft: 'auto' }}>{ps.icon} {ps.label.toUpperCase()}</span>
        </div>

        <div className="shop-chrome" style={{ display: 'flex', gap: 12, padding: 16, flexWrap: 'wrap' }}>
          {/* Same as the drawing view: mark it the moment it is done, without
              going back to the list first. */}
          {PICK_KEYS.map(key => {
            const st = { key, ...PICK_STATUS[key], solid: PICK_SOLID[key] };
            return (
              <button
                key={key}
                onClick={() => setPickStatus(o, key)}
                disabled={pickBusyId === o.id}
                style={statusButton(st, resolvePickStatus(o) === key, pickBusyId === o.id)}
              >
                {st.icon} {st.label}
              </button>
            );
          })}
        </div>

        <div style={{ padding: 16 }}>
          <div className="shop-pick-card" style={{ background: '#FFFFFF', border: `1px solid ${C_BORDER}`, borderRadius: 12, padding: 16 }}>
            <p style={{ color: C_TEXT, fontSize: 26, fontWeight: 800, margin: '0 0 2px 0' }}>
              {o.customerName || '—'}
            </p>
            <p style={{ color: C_TEXT_2, fontSize: 17, margin: '0 0 4px 0' }}>
              Order #{o.docNumber}
              {o.poNumber ? `  ·  PO ${o.poNumber}` : ''}
            </p>
            {o.isDelivery && o.deliveryStreet && (
              <p style={{ color: C_TEXT_2, fontSize: 17, margin: '0 0 4px 0' }}>
                🚚 {[o.deliveryStreet, o.deliveryCity, o.deliveryState, o.deliveryZip].filter(Boolean).join(', ')}
              </p>
            )}
            <p style={{ color: C_TEXT_2, fontSize: 15, margin: '0 0 10px 0' }}>
              Sent by {o.pickRequestedBy || '—'}{o.pickRequestedAt ? ' · ' + fmtDateTime(o.pickRequestedAt) : ''}
            </p>

            {/* What is still outstanding, in one line, at the top where it is
                read before the table. Prints too — a printed list that does not
                say what is already collected is worse than no list. */}
            {(() => {
              const outstanding = backOrderCount(o);
              const total = collectableCount(o);
              const done = outstanding === 0 && total > 0;
              return (
                <p style={{
                  margin: '0 0 14px 0', padding: '10px 14px', borderRadius: 10,
                  background: done ? '#E4F5EC' : '#FFF3DC',
                  color: done ? '#0B5B44' : '#7A4E00',
                  fontSize: 20, fontWeight: 800,
                }}>
                  {total === 0
                    ? 'No items on this order'
                    : done
                      ? `All ${total} items collected`
                      : `${outstanding} of ${total} items still to collect`}
                </p>
              );
            })()}

            {items.length === 0 ? (
              <p style={{ color: C_TEXT_2, fontSize: 20, padding: '24px 0' }}>No items on this order.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#03045E', color: '#FFFFFF' }}>
                    <th style={thPick(90, 'center')}>Got it</th>
                    <th style={thPick(70, 'center')}>Qty</th>
                    <th style={thPick(80, 'left')}>UM</th>
                    <th style={thPick(null, 'left')}>Description</th>
                    <th style={thPick(150, 'left')}>SKU</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Qty, unit, description, SKU. NO unit price, NO line total,
                      NO subtotal, tax or grand total — see the header note. */}
                  {items.map((item, i) => (
                    item.isTextNote ? (
                      <tr key={i} style={{ background: i % 2 === 0 ? '#FFFFFF' : '#F4F8FC' }}>
                        <td colSpan={5} style={{ ...tdPick, fontStyle: 'italic', color: C_TEXT_2 }}>
                          {item.description}
                        </td>
                      </tr>
                    ) : (
                      <tr key={i} style={{ background: i % 2 === 0 ? '#FFFFFF' : '#F4F8FC' }}>
                        {/* A BUTTON drawn as a checkbox, not an <input>. A real
                            checkbox is a ~13px target no glove can hit, and it
                            prints as an empty square on most browsers — this one
                            carries its own ✓ glyph, so a printed sheet shows
                            what has already been collected. */}
                        <td style={{ ...tdPick, textAlign: 'center' }}>
                          {(() => {
                            const picked = isItemPicked(item, o);
                            const tickable = item.id !== undefined && item.id !== null;
                            return (
                              <button
                                onClick={() => toggleCollected(o, item)}
                                disabled={!tickable}
                                aria-pressed={picked}
                                aria-label={(picked ? 'Collected: ' : 'Not collected: ') + (item.description || '')}
                                title={tickable ? 'Tap when the customer takes this' : 'This line has no id and cannot be ticked'}
                                style={tickBox(picked, tickable)}
                              >
                                {picked ? '✓' : ''}
                              </button>
                            );
                          })()}
                        </td>
                        <td style={{ ...tdPick, textAlign: 'center', fontWeight: 800, fontSize: 22 }}>{item.qty}</td>
                        <td style={{ ...tdPick, color: C_TEXT_2 }}>{item.unit}</td>
                        <td style={tdPick}>
                          {item.description}
                          {item.isReturnCredit && (
                            <span style={{ marginLeft: 8, fontSize: 14, fontWeight: 800, color: '#A32D2D' }}>RETURN</span>
                          )}
                          {/* Carried over from the packing slip: it tells the
                              welder whether the item is even in the building. */}
                          {item.isNonStockCustom && (
                            <span style={{ marginLeft: 8, fontSize: 14, fontWeight: 800, color: '#854F0B' }}>NON-STOCK</span>
                          )}
                        </td>
                        <td style={{ ...tdPick, fontFamily: 'monospace', color: C_TEXT_2 }}>{item.sku}</td>
                      </tr>
                    )
                  ))}
                </tbody>
              </table>
            )}

            {/* ── PICKUP SIGNATURE ─────────────────────────────────────
                NOT GATED ON PICK STATUS, deliberately. A customer signs for
                what they are carrying out of the door, and that happens on
                half-picked orders as often as on finished ones. Tying the pad
                to "Ready" would mean the welder marking an order done he has
                not done, just to get a signature — which is how a status field
                stops meaning anything. */}
            <div className="shop-chrome" style={{ marginTop: 24 }}>
              {signing ? (
                <div style={{
                  border: `2px solid ${C_ACCENT}`, borderRadius: 14,
                  background: '#F7FAFD', padding: 16,
                }}>
                  <p style={{ margin: '0 0 10px 0', fontSize: 20, fontWeight: 800, color: C_TEXT }}>
                    ✍ Pickup signature
                  </p>
                  {/* KEYED, and the key must differ from the read-only one
                      below. Both branches of this ternary render a SignaturePad
                      in the same slot of the same shape of div, so React
                      reconciles them as the SAME component and keeps its state.
                      Without distinct keys, tapping "Sign again" would reopen
                      the pad with the previous signature's strokes still on it
                      and the next save would append to them. */}
                  <SignaturePad
                    key="sig-capture"
                    onSave={(sig) => saveSignature(o, sig)}
                    onCancel={() => setSigning(false)}
                    busy={sigBusy}
                    defaultName={o.customerName || ''}
                  />
                </div>
              ) : hasSignature(o) ? (
                <div style={{
                  border: `2px solid ${C_BORDER}`, borderRadius: 14,
                  background: C_CARD, padding: 16,
                }}>
                  <p style={{ margin: '0 0 10px 0', fontSize: 18, fontWeight: 800, color: '#0B5B44' }}>
                    ✓ Signed for
                  </p>
                  <SignaturePad key="sig-view" value={o} readOnly />
                  {/* Redoing a signature is explicitly allowed — a finger on
                      glass goes wrong, and a bad signature nobody can replace
                      is worse than no signature. */}
                  <button
                    onClick={() => setSigning(true)}
                    disabled={sigBusy}
                    style={{ ...btnBack(sigBusy), marginTop: 10 }}
                  >
                    ✍ Sign again
                  </button>
                </div>
              ) : (
                <button onClick={() => setSigning(true)} style={btnViewDrawing}>
                  ✍ Capture Signature
                </button>
              )}
            </div>

            {/* PRINTS ON THE PICKING LIST. Outside .shop-chrome, so unlike the
                buttons above it this one reaches paper — signed if it has been
                signed, and a blank ruled line if it has not, so a sheet carried
                out to the yard can still be signed with a pen. */}
            <PrintSignatureBlock doc={o} title="Received by" />
          </div>
        </div>
      </div>
    );
  }

  // ── Boards ──
  //
  // BOTH tabs are boards, so the page is exactly one viewport tall and never
  // scrolls; the columns scroll inside it. That is what lets a long Ready column
  // sit beside a short Not pulled one without pushing it off the screen.
  return (
    <div style={{
      height: '100vh', background: C_BG, fontFamily: 'Manrope, sans-serif',
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, padding: '16px 20px', background: C_CARD, borderBottom: `2px solid ${C_BORDER}`,
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <div>
          <p style={{ color: C_TEXT, fontSize: 26, fontWeight: 800, margin: 0 }}>🔧 Shop</p>
          <p style={{ color: C_TEXT_2, fontSize: 16, margin: '2px 0 0 0' }}>
            Signed in as <strong style={{ color: C_TEXT }}>{userName || '—'}</strong>
          </p>
        </div>
        <button onClick={handleSignOut} style={{ ...btnBig, minWidth: 130 }}>Sign out</button>
      </div>

      {/* Section switch. Full-width halves so each is an unmissable thumb
          target, and each carries a count of OUTSTANDING work so the welder
          knows what is waiting on the other side without switching. */}
      <div style={{ display: 'flex', gap: 12, padding: '16px 16px 0 16px' }}>
        {[
          { key: 'gates', label: '🔧 Gates', count: gatesOutstanding },
          { key: 'orders', label: '📦 Orders', count: ordersOutstanding },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={btnTab(tab === t.key)}>
            {t.label}
            {t.count > 0 && (
              <span style={{
                marginLeft: 10, background: tab === t.key ? '#FFFFFF' : C_ACCENT,
                color: tab === t.key ? C_ACCENT : '#FFFFFF',
                borderRadius: 999, padding: '2px 12px', fontSize: 18, fontWeight: 800,
              }}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'orders' ? (
      /* ── THE ORDERS BOARD ─────────────────────────────────────────────
         Not pulled | Back order | Ready — the same shell as the Gates board,
         and for the same reason: position answers "what is outstanding" so
         nobody has to read every badge to find out.

         The difference is that the middle column is not a stored state. See
         pickColumnOf() above for the rule; the short version is that partly
         collected wins, and otherwise the stored pickStatus decides.

         Buttons move the STORED status, which is the only thing a person can
         set. There are two stored states, so a card carries exactly one move:
         forward to Ready, or back to Not pulled. Back order is reached and left
         by ticking items in the item list, never by a button, because it is a
         fact about what the customer took rather than a decision anyone makes.

         NO DRAG AND DROP here either — buttons only. */
      ordersLoading ? (
        <p style={{ color: C_TEXT_2, fontSize: 20, textAlign: 'center', padding: 40 }}>Loading…</p>
      ) : ordersError ? (
        <p style={{ color: '#A32D2D', fontSize: 20, textAlign: 'center', padding: 40 }}>
          Could not load orders. Check the connection.
        </p>
      ) : (
      <div style={boardGrid}>
        {PICK_COLUMNS.map(col => {
          const cards = orderColumns[col.key] || [];
          return (
            <BoardColumn key={col.key} col={col} count={cards.length}>
              {cards.length === 0 ? (
                <p style={emptyColumn}>Nothing here.</p>
              ) : cards.map(o => {
                const ps = pickOf(o);
                const total = collectableCount(o);
                const outstanding = backOrderCount(o);
                const busy = pickBusyId === o.id;
                return (
                  <div key={o.id} style={{
                    background: C_CARD, border: `2px solid ${C_BORDER}`,
                    borderLeft: `8px solid ${col.solid}`, borderRadius: 12,
                    padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8,
                    /* A CONTAINER, NOT A CONTROL — same as the gate card. */
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ color: C_ACCENT, fontFamily: 'monospace', fontSize: 19, fontWeight: 800 }}>
                        #{o.docNumber || '—'}
                      </span>
                      {/* The stored status is shown ONLY in Back order, where the
                          column does not already say it. In the other two columns
                          the heading and the pill would say the same word twice. */}
                      {col.key === 'backorder' && (
                        <span style={{ ...pill(ps), fontSize: 13, padding: '2px 8px' }}>
                          {ps.icon} {ps.label.toUpperCase()}
                        </span>
                      )}
                    </div>

                    <p style={{ color: C_TEXT, fontSize: 20, fontWeight: 700, margin: 0, overflowWrap: 'anywhere' }}>
                      {o.customerName || '—'}
                    </p>
                    <p style={{ color: C_TEXT_2, fontSize: 14, margin: 0, overflowWrap: 'anywhere' }}>
                      Sent by {o.pickRequestedBy || '—'}
                      {o.pickRequestedAt ? ' · ' + fmtDateTime(o.pickRequestedAt) : ''}
                    </p>
                    {ps.key === 'Ready' && o.pickStatusBy && (
                      <p style={{ color: C_TEXT_2, fontSize: 13, margin: 0 }}>
                        Ready by {o.pickStatusBy}
                        {o.pickStatusAt ? ' · ' + fmtDateTime(o.pickStatusAt) : ''}
                      </p>
                    )}

                    {/* What is still outstanding, in words rather than a bare
                        number: "3 of 11" alone reads as a fraction of something
                        and nobody should have to guess which way round it is. */}
                    <p style={{
                      color: outstanding > 0 ? '#8C2020' : '#0B5B44',
                      fontSize: 14, fontWeight: 800, margin: 0,
                    }}>
                      {total === 0
                        ? 'No items on this order'
                        : outstanding === 0
                          ? `All ${total} items collected`
                          : outstanding === total
                            ? `${total} items, none collected`
                            : `${outstanding} of ${total} items outstanding`}
                    </p>

                    <button
                      onClick={() => { setSigning(false); setOpenOrder(o); }}
                      style={btnViewDrawing}
                    >
                      📦 View Items
                    </button>

                    {/* One move per card, because there are two stored states.
                        Named by destination, like the gate buttons. */}
                    {ps.key === 'Pending' ? (
                      <button
                        onClick={() => setPickStatus(o, 'Ready')}
                        disabled={busy}
                        style={btnForward({ solid: PICK_SOLID['Ready'] }, busy)}
                      >
                        🟢 Ready
                      </button>
                    ) : (
                      <button
                        onClick={() => setPickStatus(o, 'Pending')}
                        disabled={busy}
                        style={btnBack(busy)}
                      >
                        ↩ {PICK_STATUS['Pending'].label}
                      </button>
                    )}
                  </div>
                );
              })}
            </BoardColumn>
          );
        })}
      </div>
      )
      ) : (
      /* ── THE GATES BOARD ──────────────────────────────────────────────
         Three columns, left to right in the order work moves through them, all
         three on screen at once. A flat list made you read every card's badge
         to answer "what is still outstanding"; position answers it now, and the
         badge is only confirmation.

         GRID, NOT FLEX-WRAP. Three equal tracks that never wrap and never
         overflow: minmax(0, 1fr) is what stops a long customer name widening
         its track and pushing the board sideways. A landscape tablet must never
         scroll horizontally — the third column being half off-screen is exactly
         the failure this rebuild exists to fix.

         NO DRAG AND DROP, DELIBERATELY. Cards move by tapping a button. Gloved
         hands and dragging do not mix, and a dropped card that lands in the
         wrong column is worse than a tap that does nothing. Nothing here is
         draggable and no drag library is imported. */
      loading ? (
        <p style={{ color: C_TEXT_2, fontSize: 20, textAlign: 'center', padding: 40 }}>Loading…</p>
      ) : error ? (
        <p style={{ color: '#A32D2D', fontSize: 20, textAlign: 'center', padding: 40 }}>
          Could not load drawings. Check the connection.
        </p>
      ) : (
      <div style={boardGrid}>
        {STATUSES.map(col => {
          const cards = columns[col.key] || [];
          return (
            <BoardColumn key={col.key} col={col} count={cards.length}>
              {cards.length === 0 ? (
                <p style={emptyColumn}>Nothing here.</p>
              ) : cards.map(d => {
                  const s = statusOf(d);
                  const at = STATUSES.findIndex(x => x.key === s.key);
                  const forward = STATUSES[at + 1] || null;   // Complete has none
                  const back = STATUSES[at - 1] || null;      // Submitted has none
                  const busy = savingId === d.id;
                  return (
                    <div key={d.id} style={{
                      background: C_CARD, border: `2px solid ${C_BORDER}`,
                      borderLeft: `8px solid ${s.solid}`, borderRadius: 12,
                      padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8,
                      /* A CONTAINER, NOT A CONTROL — unchanged from the list.
                         No onClick, no cursor, no role: reaching past a card
                         with a gloved hand must not open anything. */
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ color: C_ACCENT, fontFamily: 'monospace', fontSize: 19, fontWeight: 800 }}>
                          {d.drawingNumber || '—'}
                        </span>
                        {(d.quantity || 1) > 1 && (
                          <span style={{
                            background: '#03045E', color: '#FFFFFF', fontSize: 15, fontWeight: 800,
                            padding: '2px 9px', borderRadius: 7,
                          }}>
                            QTY {d.quantity}
                          </span>
                        )}
                      </div>

                      {/* overflowWrap so a long unbroken customer name breaks
                          rather than widening the column. */}
                      <p style={{
                        color: C_TEXT, fontSize: 20, fontWeight: 700, margin: 0,
                        overflowWrap: 'anywhere',
                      }}>
                        {d.customerName || '—'}
                      </p>
                      <p style={{ color: C_TEXT_2, fontSize: 16, margin: 0, overflowWrap: 'anywhere' }}>
                        {d.type || '—'} · {inches(d.openingIn)} × {inches(d.heightIn)}
                      </p>
                      {d.shopStatusBy && (
                        <p style={{ color: C_TEXT_2, fontSize: 13, margin: 0 }}>
                          {s.label} by {d.shopStatusBy} · {fmtDate(d.shopStatusAt)}
                        </p>
                      )}

                      <button onClick={() => openSheet(d)} style={btnViewDrawing}>
                        📄 View Drawing
                      </button>

                      {/* FORWARD — the one thing a welder does most, so it is
                          the only filled button on the card.

                          LABELLED BY DESTINATION, NOT DIRECTION. "Start" and
                          "Complete" say what happens to the gate; an arrow says
                          only which way a card slides, which is the screen's
                          business and not the work's. */}
                      {forward && (
                        <button
                          onClick={() => setStatus(d, forward.key)}
                          disabled={busy}
                          style={btnForward(forward, busy)}
                        >
                          {forward.icon} {forward.key === 'In Progress' ? 'Start' : forward.label}
                        </button>
                      )}

                      {/* BACK — a correction, not part of the flow, so it is
                          clearly secondary: no fill, thinner border, muted, and
                          well under half the forward button's height. Named by
                          where the card lands, with the return glyph saying it
                          is a step back. */}
                      {back && (
                        <button
                          onClick={() => setStatus(d, back.key)}
                          disabled={busy}
                          style={btnBack(busy)}
                        >
                          ↩ {back.label}
                        </button>
                      )}
                    </div>
                  );
                })}
            </BoardColumn>
          );
        })}
      </div>
      )
      )}
    </div>
  );
}

// ── The board shell ──
// Both tabs are the same board with different cards in it, so the column — its
// heading, its count and its own scroller — is written once. Only what goes
// INSIDE a column differs, and that is what the children are.
function BoardColumn({ col, count, children }) {
  return (
    <section style={{
      display: 'flex', flexDirection: 'column',
      // minHeight:0 on both the grid and the section is what actually lets the
      // scroller below shrink. Without it a flex child refuses to go below its
      // content height and every column grows the page instead of scrolling.
      minHeight: 0,
      background: '#F7FAFD',
      border: `2px solid ${C_BORDER}`,
      borderTop: `6px solid ${col.solid}`,
      borderRadius: 14, overflow: 'hidden',
    }}>
      {/* Heading carries the same glyph, colour and word as the cards beneath
          it, so a card and its column are obviously the same fact. */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        padding: '10px 12px', background: col.bg, borderBottom: `2px solid ${C_BORDER}`,
      }}>
        <span style={{
          color: col.fg, fontSize: 18, fontWeight: 800, letterSpacing: 0.5,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {col.icon} {col.label.toUpperCase()}
        </span>
        <span style={countChip(col)}>{count}</span>
      </div>

      {/* EACH COLUMN SCROLLS ON ITS OWN. Forty finished gates in Complete must
          not push Submitted off the bottom of the screen — the column with the
          least work is the one the welder most needs to see. */}
      <div style={{
        flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
        padding: 12, display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        {children}
      </div>
    </section>
  );
}

// Three equal tracks that never wrap and never overflow: minmax(0, 1fr) is what
// stops a long customer name widening its track and pushing the board sideways.
// A landscape tablet must never scroll horizontally — the third column half
// off-screen is exactly the failure these boards exist to fix.
const boardGrid = {
  flex: 1, minHeight: 0,
  display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 12, padding: '14px 16px 16px 16px',
};

const emptyColumn = {
  color: C_TEXT_2, fontSize: 16, textAlign: 'center', padding: '18px 4px', margin: 0,
};

// ── Shared styles ──
// Every target is at least 56px tall: a comfortable thumb press through a
// glove, well above the ~44px minimum a bare fingertip needs.
const btnBig = {
  background: C_CARD, border: `2px solid ${C_BORDER}`, color: C_TEXT,
  padding: '14px 22px', borderRadius: 12, fontSize: 18, fontWeight: 700,
  cursor: 'pointer', fontFamily: 'inherit', minHeight: 56,
};

// ── CARD BUTTON SIZES ────────────────────────────────────────────────
// The old card carried View Drawing at 72px plus THREE status buttons at 64px,
// stacked: about 210px of button under every gate. That was right when the
// buttons were the only way to tell one status from another, and it is why only
// two or three cards fitted a screen.
//
// The column now states the status, so a card needs one forward button and one
// small correction. With four targets down to two there is room to shrink them,
// and shrinking them is what puts a useful number of cards in a column:
//
//   View Drawing  52px   still the biggest thing on the card — reading the
//                        drawing matters more than recording progress on it
//   Forward       50px   the action taken most; comfortably past the ~44px a
//                        gloved thumb needs, and the only filled button here
//   Back          36px   deliberately below thumb-comfort. It is a correction,
//                        not part of the flow, and it should take a little
//                        aim — a mis-tap that sends a finished gate back to
//                        Submitted is a gate somebody rebuilds.
//
// Nothing on this card is smaller than 36px, and the two buttons that carry the
// work are both 50px or more.
const btnViewDrawing = {
  width: '100%',
  minHeight: 52,
  background: C_ACCENT,
  color: '#FFFFFF',
  border: `2px solid ${C_ACCENT}`,
  borderRadius: 10,
  fontSize: 18,
  fontWeight: 800,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

// The forward move, filled in the DESTINATION's colour so the card previews
// where it is going before the welder commits to the tap.
const btnForward = (dest, busy) => ({
  width: '100%',
  minHeight: 50,
  background: dest.solid,
  color: '#FFFFFF',
  border: `2px solid ${dest.solid}`,
  borderRadius: 10,
  fontSize: 18,
  fontWeight: 800,
  cursor: busy ? 'default' : 'pointer',
  opacity: busy ? 0.6 : 1,
  fontFamily: 'inherit',
});

// The correction. Unfilled, thinner-bordered, muted and short: everything about
// it says "not the thing you came here to press".
const btnBack = (busy) => ({
  width: '100%',
  minHeight: 36,
  background: 'transparent',
  color: C_TEXT_2,
  border: `1px solid ${C_BORDER}`,
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 700,
  cursor: busy ? 'default' : 'pointer',
  opacity: busy ? 0.6 : 1,
  fontFamily: 'inherit',
});

// The count in a column heading. Reversed out of the status colour so it reads
// as part of the heading rather than as another badge to interpret.
const countChip = (st) => ({
  background: st.solid,
  color: '#FFFFFF',
  fontSize: 16,
  fontWeight: 800,
  minWidth: 30,
  textAlign: 'center',
  padding: '2px 9px',
  borderRadius: 999,
  flexShrink: 0,
});

// Close is the escape hatch, so it reads as one.
const btnClose = {
  background: '#FDEAEA',
  border: '2px solid #A32D2D',
  color: '#8C2020',
  padding: '14px 26px',
  borderRadius: 12,
  fontSize: 20,
  fontWeight: 800,
  cursor: 'pointer',
  fontFamily: 'inherit',
  minHeight: 60,
};

const btnZoom = (disabled) => ({
  background: C_CARD,
  border: `2px solid ${C_BORDER}`,
  color: C_TEXT,
  width: 68,
  minHeight: 60,
  borderRadius: 12,
  fontSize: 30,
  fontWeight: 800,
  lineHeight: 1,
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.4 : 1,
  fontFamily: 'inherit',
});

// Half-width each, so both sections are a full thumb target.
const btnTab = (active) => ({
  flex: '1 1 0',
  minHeight: 68,
  background: active ? C_ACCENT : C_CARD,
  color: active ? '#FFFFFF' : C_TEXT_2,
  border: `2px solid ${active ? C_ACCENT : C_BORDER}`,
  borderRadius: 14,
  fontSize: 22,
  fontWeight: 800,
  cursor: 'pointer',
  fontFamily: 'inherit',
});

// A checkbox a gloved thumb can hit — 52px, well past the ~44px a bare
// fingertip needs and four times the size of a browser default.
const tickBox = (picked, enabled) => ({
  width: 52,
  height: 52,
  borderRadius: 10,
  border: `3px solid ${enabled ? (picked ? '#0F6E56' : '#54657A') : '#D3E0EC'}`,
  background: picked ? '#0F6E56' : '#FFFFFF',
  color: '#FFFFFF',
  fontSize: 30,
  fontWeight: 800,
  lineHeight: 1,
  cursor: enabled ? 'pointer' : 'not-allowed',
  opacity: enabled ? 1 : 0.4,
  fontFamily: 'inherit',
  padding: 0,
  // The tick must survive a black-and-white printer, where the green fill is
  // just grey — printColorAdjust keeps the fill, and the glyph carries it
  // regardless.
  WebkitPrintColorAdjust: 'exact',
  printColorAdjust: 'exact',
});

// Picking table: big enough to read standing up, tight enough to fit a page.
const thPick = (width, align) => ({
  width: width || undefined,
  textAlign: align,
  padding: '10px 12px',
  fontSize: 16,
  fontWeight: 800,
  letterSpacing: 0.5,
});
const tdPick = {
  padding: '12px',
  fontSize: 19,
  color: '#03045E',
  borderTop: '1px solid #D3E0EC',
  verticalAlign: 'top',
};

const pill = (s) => ({
  background: s.bg, color: s.fg, border: `2px solid ${s.solid}`,
  fontSize: 16, fontWeight: 800, letterSpacing: 0.5,
  padding: '4px 12px', borderRadius: 8, whiteSpace: 'nowrap',
});

const statusButton = (st, active, busy) => ({
  flex: '1 1 150px',
  minHeight: 64,
  background: active ? st.solid : C_CARD,
  color: active ? '#FFFFFF' : st.fg,
  border: `2px solid ${st.solid}`,
  borderRadius: 12,
  fontSize: 19,
  fontWeight: 800,
  cursor: busy ? 'default' : 'pointer',
  opacity: busy ? 0.6 : 1,
  fontFamily: 'inherit',
});
